import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getStrategyById, getVariantById } from '@/config/funnelStrategies';
import {
  Download, Share2, Copy as CopyIcon, Check,
  FileText, Mail, MessageCircle, Image,
  ExternalLink, RefreshCw, ChevronRight,
  CheckCircle2, Circle, Link2, Loader2,
  Rocket, BarChart3, TrendingUp, DollarSign, MousePointerClick, Eye,
  AlertCircle
} from 'lucide-react';

const FunnelDashboard = ({ funnel, assets, onRefresh, onGoToStep }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const strategy = getStrategyById(funnel?.strategy);
  const variant = getVariantById(funnel?.strategy, funnel?.strategy_variant);

  // Manual checklist state from localStorage
  const [manualChecks, setManualChecks] = useState({});
  const [generatingLink, setGeneratingLink] = useState(false);
  const [previewLink, setPreviewLink] = useState(funnel?.preview_token || null);
  
  // Ad Metrics state
  const [adMetrics, setAdMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [metricsDateRange, setMetricsDateRange] = useState('last_30_days');
  
  // Real asset counts from database
  const [realAssetCounts, setRealAssetCounts] = useState({
    landings: { generated: 0, expected: 0 },
    emails: { generated: 0, expected: 0 },
    whatsapp: { generated: 0, expected: 0 },
    creativos: { generated: 0, expected: 0 },
    adCopies: { generated: 0, expected: 0 }
  });
  const [loadingAssets, setLoadingAssets] = useState(true);

  // Load manual checks from localStorage
  useEffect(() => {
    if (funnel?.id) {
      const saved = localStorage.getItem(`checklist_${funnel.id}`);
      if (saved) {
        try {
          setManualChecks(JSON.parse(saved));
        } catch (e) {
          // ignore
        }
      }
    }
  }, [funnel?.id]);

  // Save manual checks to localStorage
  const toggleManualCheck = (checkId) => {
    const newChecks = { ...manualChecks, [checkId]: !manualChecks[checkId] };
    setManualChecks(newChecks);
    if (funnel?.id) {
      localStorage.setItem(`checklist_${funnel.id}`, JSON.stringify(newChecks));
    }
  };

  // Calculate real asset counts from funnel data and API
  const calculateAssetCounts = useCallback(async () => {
    if (!funnel?.id) return;
    
    setLoadingAssets(true);
    
    try {
      // Get expected counts from assets_config or strategy defaults
      const assetsConfig = funnel?.assets_config || {};
      const strategyDefaults = variant?.defaultAssets || {};
      
      // Expected counts
      const expectedLandings = Array.isArray(assetsConfig.landings) 
        ? assetsConfig.landings.length 
        : (strategyDefaults.landings?.length || 6);
      
      const expectedEmails = typeof assetsConfig.emails === 'number' 
        ? assetsConfig.emails 
        : (assetsConfig.emailNames?.length || strategyDefaults.emails || 5);
      
      const expectedWhatsapp = typeof assetsConfig.whatsapp === 'number'
        ? assetsConfig.whatsapp
        : (strategyDefaults.whatsapp || 2);
      
      const expectedAdCopies = typeof assetsConfig.adCopies === 'number'
        ? assetsConfig.adCopies
        : (strategyDefaults.adCopies || 4);
      
      const expectedCreativos = typeof assetsConfig.adCreatives === 'number'
        ? assetsConfig.adCreatives
        : (strategyDefaults.adCreatives || 5);
      
      // Generated counts - read from actual data
      
      // 1. Landings - from funnel.landings (dict)
      const generatedLandings = funnel?.landings 
        ? (typeof funnel.landings === 'object' ? Object.keys(funnel.landings).length : 0)
        : 0;
      
      // 2. Emails - check funnel.sequences.emails, funnel.assets.emails, or localStorage
      let generatedEmails = 0;
      if (funnel?.sequences?.emails) {
        const emailsObj = funnel.sequences.emails;
        generatedEmails = typeof emailsObj === 'object' 
          ? Object.keys(emailsObj).filter(k => emailsObj[k]).length 
          : 0;
      } else if (funnel?.assets?.emails) {
        const emailsArr = funnel.assets.emails;
        generatedEmails = Array.isArray(emailsArr) ? emailsArr.length : 0;
      } else {
        // Check localStorage as fallback
        try {
          const savedApproved = localStorage.getItem(`sequences_approved_emails_${funnel.id}`);
          const savedEmails = localStorage.getItem(`sequences_emails_${funnel.id}`);
          if (savedApproved) {
            const parsed = JSON.parse(savedApproved);
            generatedEmails = Object.keys(parsed).filter(k => parsed[k] === true).length;
          } else if (savedEmails) {
            const parsed = JSON.parse(savedEmails);
            generatedEmails = Object.keys(parsed).filter(k => parsed[k]).length;
          }
        } catch (e) { /* ignore */ }
      }
      
      // 3. WhatsApp - similar to emails
      let generatedWhatsapp = 0;
      if (funnel?.sequences?.whatsapp) {
        const waObj = funnel.sequences.whatsapp;
        generatedWhatsapp = typeof waObj === 'object' 
          ? Object.keys(waObj).filter(k => waObj[k]).length 
          : 0;
      } else {
        try {
          const savedApproved = localStorage.getItem(`sequences_approved_whatsapp_${funnel.id}`);
          const savedWa = localStorage.getItem(`sequences_whatsapp_${funnel.id}`);
          if (savedApproved) {
            const parsed = JSON.parse(savedApproved);
            generatedWhatsapp = Object.keys(parsed).filter(k => parsed[k] === true).length;
          } else if (savedWa) {
            const parsed = JSON.parse(savedWa);
            generatedWhatsapp = Object.keys(parsed).filter(k => parsed[k]).length;
          }
        } catch (e) { /* ignore */ }
      }
      
      // 4. Ad Copies - from funnel.assets.adsCopyCaptacion + adsCopyVenta
      const copyCaptacion = funnel?.assets?.adsCopyCaptacion;
      const copyVenta = funnel?.assets?.adsCopyVenta;
      const generatedAdCopies = 
        (Array.isArray(copyCaptacion) ? copyCaptacion.length : 0) +
        (Array.isArray(copyVenta) ? copyVenta.length : 0);
      
      // 5. Creativos - fetch from separate endpoint
      let generatedCreativos = 0;
      if (funnel?.creativos && Array.isArray(funnel.creativos)) {
        generatedCreativos = funnel.creativos.length;
      } else {
        try {
          const creativosRes = await api.get(`/funnels/${funnel.id}/creativos`);
          if (Array.isArray(creativosRes.data)) {
            generatedCreativos = creativosRes.data.length;
          }
        } catch (e) {
          console.error('Error fetching creativos:', e);
        }
      }
      
      setRealAssetCounts({
        landings: { 
          generated: Math.min(generatedLandings, expectedLandings), // Never exceed expected
          expected: expectedLandings 
        },
        emails: { 
          generated: Math.min(generatedEmails, expectedEmails),
          expected: expectedEmails 
        },
        whatsapp: { 
          generated: Math.min(generatedWhatsapp, expectedWhatsapp),
          expected: expectedWhatsapp 
        },
        creativos: { 
          generated: Math.min(generatedCreativos, expectedCreativos),
          expected: expectedCreativos 
        },
        adCopies: { 
          generated: Math.min(generatedAdCopies, expectedAdCopies),
          expected: expectedAdCopies 
        }
      });
      
    } catch (err) {
      console.error('Error calculating asset counts:', err);
    } finally {
      setLoadingAssets(false);
    }
  }, [funnel, variant, api]);

  // Load asset counts when funnel changes
  useEffect(() => {
    calculateAssetCounts();
  }, [calculateAssetCounts]);

  // Load ad metrics for this funnel
  const loadAdMetrics = useCallback(async () => {
    if (!funnel?.id) return;
    setLoadingMetrics(true);
    try {
      const res = await api.get(`/funnels/${funnel.id}/metrics?date_range=${metricsDateRange}`);
      setAdMetrics(res.data);
    } catch (err) {
      console.error('Error loading ad metrics:', err);
      setAdMetrics(null);
    } finally {
      setLoadingMetrics(false);
    }
  }, [funnel?.id, metricsDateRange, api]);

  // Load metrics when funnel changes or date range changes
  useEffect(() => {
    if (funnel?.id) {
      loadAdMetrics();
    }
  }, [funnel?.id, loadAdMetrics]);

  // Calculate total progress
  const totalExpected = 
    realAssetCounts.landings.expected + 
    realAssetCounts.emails.expected + 
    realAssetCounts.whatsapp.expected + 
    realAssetCounts.creativos.expected;
    
  const totalGenerated = 
    realAssetCounts.landings.generated + 
    realAssetCounts.emails.generated + 
    realAssetCounts.whatsapp.generated + 
    realAssetCounts.creativos.generated;
    
  const progressPercent = totalExpected > 0 
    ? Math.min(100, Math.round((totalGenerated / totalExpected) * 100))
    : 0;

  const isComplete = progressPercent >= 80;

  // Asset cards data
  const assetCards = [
    {
      id: 'landings',
      title: 'Landings',
      icon: FileText,
      color: 'brand',
      current: realAssetCounts.landings.generated,
      expected: realAssetCounts.landings.expected,
      step: 3,
      description: 'Páginas de captura y venta'
    },
    {
      id: 'emails',
      title: 'Emails',
      icon: Mail,
      color: '#F59E0B',
      current: realAssetCounts.emails.generated,
      expected: realAssetCounts.emails.expected,
      step: 4,
      description: 'Secuencia de nurturing'
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      current: realAssetCounts.whatsapp.generated,
      expected: realAssetCounts.whatsapp.expected,
      step: 4,
      description: 'Mensajes de seguimiento'
    },
    {
      id: 'creativos',
      title: 'Creativos',
      icon: Image,
      color: '#EC4899',
      current: realAssetCounts.creativos.generated,
      expected: realAssetCounts.creativos.expected,
      step: 5,
      description: 'Imágenes para anuncios'
    }
  ];

  // Check if platform is connected
  const isPlatformConnected = !!(
    funnel?.client_integrations?.meta?.connected ||
    funnel?.client_integrations?.google_ads?.connected ||
    funnel?.client_integrations?.ghl?.connected ||
    funnel?.client_integrations?.wordpress?.connected
  );

  // Launch checklist
  const launchChecklist = [
    {
      id: 'brief',
      label: 'Brief completado',
      description: 'Producto, avatar y propuesta de valor definidos',
      completed: !!(funnel?.brief?.productName || funnel?.brief?.product_name) && 
                 !!(funnel?.brief?.avatarName || funnel?.brief?.avatar_name),
      manual: false
    },
    {
      id: 'landings',
      label: 'Landings generadas',
      description: `${realAssetCounts.landings.generated} de ${realAssetCounts.landings.expected} páginas`,
      completed: realAssetCounts.landings.generated > 0,
      manual: false
    },
    {
      id: 'emails',
      label: 'Secuencia de emails',
      description: `${realAssetCounts.emails.generated} de ${realAssetCounts.emails.expected} emails`,
      completed: realAssetCounts.emails.generated > 0,
      manual: false
    },
    {
      id: 'whatsapp',
      label: 'Mensajes de WhatsApp',
      description: `${realAssetCounts.whatsapp.generated} de ${realAssetCounts.whatsapp.expected} mensajes`,
      completed: realAssetCounts.whatsapp.generated > 0,
      manual: false
    },
    {
      id: 'creativos',
      label: 'Creativos publicitarios',
      description: `${realAssetCounts.creativos.generated} de ${realAssetCounts.creativos.expected} imágenes`,
      completed: realAssetCounts.creativos.generated > 0,
      manual: false
    },
    {
      id: 'domain',
      label: 'Dominio verificado en Meta',
      description: 'Necesario para el píxel de conversión',
      completed: manualChecks.domain || false,
      manual: true
    },
    {
      id: 'integration',
      label: 'Plataforma conectada',
      description: isPlatformConnected 
        ? 'Meta/Google/GHL conectado' 
        : 'GHL, WordPress o ESP configurado',
      completed: isPlatformConnected || manualChecks.integration || false,
      manual: !isPlatformConnected
    },
    {
      id: 'approval',
      label: 'Cliente ha aprobado',
      description: 'Magic Approval Link enviado y aprobado',
      completed: manualChecks.approval || false,
      manual: true
    }
  ];

  const completedChecks = launchChecklist.filter(c => c.completed).length;
  const allChecksComplete = completedChecks === launchChecklist.length;

  // Generate preview link
  const handleGeneratePreviewLink = async () => {
    if (!funnel?.id) return;
    
    setGeneratingLink(true);
    try {
      const response = await api.post(`/funnels/${funnel.id}/generate-preview-link`);
      const token = response.data?.preview_token || response.data?.token;
      setPreviewLink(token);
      toast.success('Magic Approval Link generado');
    } catch (error) {
      toast.error('Error al generar el enlace');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyPreviewLink = () => {
    const fullUrl = `${window.location.origin}/f/${previewLink}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiado al portapapeles');
  };

  // Handle refresh
  const handleRefresh = () => {
    calculateAssetCounts();
    loadAdMetrics();
    if (onRefresh) onRefresh();
    toast.success('Datos actualizados');
  };

  // Export state
  const [exportingZip, setExportingZip] = useState(false);
  const [exportingSection, setExportingSection] = useState(null);

  // Export all content as ZIP
  const handleExportAll = async () => {
    console.log('[Export] handleExportAll called, funnel:', funnel?.id);
    if (!funnel?.id) {
      toast.error('No se pudo identificar el funnel');
      console.error('[Export] funnel.id is missing:', funnel);
      return;
    }
    
    setExportingZip(true);
    toast.loading('Generando archivo ZIP completo...', { id: 'export-zip' });
    
    try {
      const response = await api.get(`/funnels/${funnel.id}/export?section=all`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(funnel?.name || 'funnel').replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss('export-zip');
      toast.success('ZIP descargado correctamente');
    } catch (error) {
      console.error('[Export] Full error:', error);
      console.error('[Export] Error response:', error.response?.data);
      console.error('[Export] Error status:', error.response?.status);
      toast.dismiss('export-zip');
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error desconocido';
      toast.error('Error al exportar: ' + errorMsg);
    } finally {
      setExportingZip(false);
    }
  };

  // Export specific section
  const handleExportSection = async (section) => {
    console.log('[Export] handleExportSection called, section:', section, 'funnel:', funnel?.id);
    if (!funnel?.id) {
      toast.error('No se pudo identificar el funnel');
      console.error('[Export] funnel.id is missing:', funnel);
      return;
    }
    
    setExportingSection(section);
    const sectionNames = {
      landings: 'Landings',
      emails: 'Emails', 
      copies: 'Copies de Anuncios',
      creatives: 'Creativos',
      brief: 'Brief'
    };
    
    toast.loading(`Exportando ${sectionNames[section]}...`, { id: `export-${section}` });
    
    try {
      // For brief, we can request PDF format
      const format = section === 'brief' ? 'pdf' : 'zip';
      const response = await api.get(`/funnels/${funnel.id}/export?section=${section}&format=${format}`, {
        responseType: 'blob'
      });
      
      // Determine file extension
      const ext = format === 'pdf' ? 'pdf' : 'zip';
      const contentType = format === 'pdf' ? 'application/pdf' : 'application/zip';
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(funnel?.name || 'funnel').replace(/\s+/g, '_')}_${section}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(`export-${section}`);
      toast.success(`${sectionNames[section]} descargado`);
    } catch (error) {
      console.error('[Export Section] Full error:', error);
      console.error('[Export Section] Error response:', error.response?.data);
      console.error('[Export Section] Error status:', error.response?.status);
      toast.dismiss(`export-${section}`);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error desconocido';
      toast.error('Error al exportar: ' + errorMsg);
    } finally {
      setExportingSection(null);
    }
  };

  // Duplicate funnel
  const handleDuplicate = async () => {
    try {
      await api.post(`/funnels/${funnel.id}/duplicate`);
      toast.success('Funnel duplicado correctamente');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al duplicar el funnel');
    }
  };

  const handleCardClick = (step) => {
    if (onGoToStep) {
      onGoToStep(step);
    }
  };

  // Check if there are active campaigns
  const hasActiveCampaigns = adMetrics?.platforms?.meta_ads?.connected || 
                             adMetrics?.platforms?.google_ads?.connected;
  const hasAnyMetrics = adMetrics?.totals?.impressions > 0 || 
                        adMetrics?.totals?.clicks > 0 || 
                        adMetrics?.totals?.spend > 0;

  return (
    <div data-testid="funnel-dashboard" className="space-y-6">
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* SECTION 1: Hero del Funnel */}
      <div 
        className="bg-gradient-to-br from-surface via-surface to-brand/5 border border-border-subtle rounded-2xl p-8"
        style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-5">
            {/* Strategy Icon */}
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-lg"
              style={{ 
                backgroundColor: strategy?.color ? `${strategy.color}20` : 'rgba(108,99,255,0.1)',
                border: `2px solid ${strategy?.color || '#6C63FF'}30`
              }}
            >
              {strategy?.emoji || '🚀'}
            </div>
            
            <div>
              <h2 className="font-display text-2xl font-bold text-text-main mb-1">
                {funnel?.name || 'Mi Funnel'}
              </h2>
              <p className="text-text-secondary">
                {strategy?.label || funnel?.strategy} 
                {variant?.label && ` · ${variant.label}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge className={isComplete 
              ? 'bg-[#00D68F]/10 text-[#00D68F] border-[#00D68F]/20' 
              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            }>
              {isComplete ? '✓ Completo' : 'En progreso'}
            </Badge>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loadingAssets}
              className="border-border-subtle text-text-secondary"
            >
              <RefreshCw className={`w-4 h-4 ${loadingAssets ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-text-muted">Progreso del funnel</span>
            <span className="text-sm font-semibold text-brand-500">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-text-muted mt-2">
            {totalGenerated} de {totalExpected} assets generados
          </p>
        </div>
      </div>

      {/* SECTION 2: Cards de Assets */}
      <div className="grid grid-cols-2 gap-4">
        {assetCards.map((card, index) => {
          const cardProgress = card.expected > 0 
            ? Math.min(100, Math.round((card.current / card.expected) * 100))
            : 0;
          const isCardComplete = card.current >= card.expected && card.expected > 0;

          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card.step)}
              className={`bg-surface border border-border-subtle rounded-xl p-5 transition-all group ${
                onGoToStep ? 'cursor-pointer hover:border-brand/50 hover:shadow-lg hover:shadow-brand/5' : ''
              }`}
              style={{ animation: `fadeSlideUp 0.3s ease-out ${0.1 + index * 0.05}s both` }}
              data-testid={`asset-card-${card.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${card.color}15` }}
                  >
                    <card.icon 
                      className="w-5 h-5" 
                      style={{ color: card.color === 'brand' ? '#6C63FF' : card.color }} 
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-text-main">{card.title}</h4>
                    <p className="text-xs text-text-muted">{card.description}</p>
                  </div>
                </div>
                
                {onGoToStep && (
                  <ChevronRight className="w-5 h-5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary" data-testid={`${card.id}-count`}>
                    {card.current} de {card.expected}
                  </span>
                  <Badge className={isCardComplete 
                    ? 'bg-[#00D68F]/10 text-[#00D68F] text-xs' 
                    : card.current > 0 
                      ? 'bg-amber-500/10 text-amber-400 text-xs'
                      : 'bg-text-muted/10 text-text-muted text-xs'
                  }>
                    {isCardComplete ? 'Completo' : card.current > 0 ? 'Parcial' : 'Pendiente'}
                  </Badge>
                </div>
                <Progress value={cardProgress} className="h-1.5" />
              </div>
            </div>
          );
        })}
      </div>

      {/* SECTION 2.5: Ad Performance Metrics */}
      <div 
        className="bg-surface border border-border-subtle rounded-xl p-6"
        style={{ animation: 'fadeSlideUp 0.3s ease-out 0.25s both' }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500/20 to-blue-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="font-medium text-text-main">Rendimiento Publicitario</h3>
              <p className="text-xs text-text-muted">
                {hasActiveCampaigns 
                  ? 'Métricas de tus campañas activas' 
                  : 'Sin campañas activas conectadas'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <select
              value={metricsDateRange}
              onChange={(e) => setMetricsDateRange(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-lg bg-app-bg border border-border-subtle text-text-main"
              disabled={!hasActiveCampaigns}
            >
              <option value="last_7_days">Últimos 7 días</option>
              <option value="last_14_days">Últimos 14 días</option>
              <option value="last_30_days">Últimos 30 días</option>
              <option value="this_month">Este mes</option>
              <option value="last_month">Mes anterior</option>
            </select>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={loadAdMetrics}
              disabled={loadingMetrics}
              className="text-text-muted"
            >
              {loadingMetrics ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {loadingMetrics ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
          </div>
        ) : hasActiveCampaigns && hasAnyMetrics ? (
          <>
            {/* Platform Status */}
            <div className="flex items-center gap-4 mb-5">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
                adMetrics?.platforms?.meta_ads?.connected 
                  ? 'bg-blue-500/10 text-blue-400' 
                  : 'bg-text-muted/10 text-text-muted'
              }`}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Meta {adMetrics?.platforms?.meta_ads?.connected ? '✓' : '–'}</span>
              </div>
              
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
                adMetrics?.platforms?.google_ads?.connected 
                  ? 'bg-[#4285F4]/10 text-[#4285F4]' 
                  : 'bg-text-muted/10 text-text-muted'
              }`}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                </svg>
                <span>Google {adMetrics?.platforms?.google_ads?.connected ? '✓' : '–'}</span>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-app-bg rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-text-muted" />
                  <span className="text-xs text-text-muted">Impresiones</span>
                </div>
                <p className="text-2xl font-bold text-text-main">
                  {(adMetrics?.totals?.impressions || 0).toLocaleString()}
                </p>
              </div>
              
              <div className="bg-app-bg rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointerClick className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-text-muted">Clics</span>
                </div>
                <p className="text-2xl font-bold text-text-main">
                  {(adMetrics?.totals?.clicks || 0).toLocaleString()}
                </p>
                <p className="text-xs text-green-400 mt-1">
                  CTR: {adMetrics?.totals?.ctr || 0}%
                </p>
              </div>
              
              <div className="bg-app-bg rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-text-muted">Gasto</span>
                </div>
                <p className="text-2xl font-bold text-text-main">
                  ${adMetrics?.totals?.spend || '0.00'}
                </p>
                <p className="text-xs text-text-muted mt-1">
                  CPC: ${adMetrics?.totals?.cpc || '0.00'}
                </p>
              </div>
              
              <div className="bg-app-bg rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-text-muted">Conversiones</span>
                </div>
                <p className="text-2xl font-bold text-text-main">
                  {adMetrics?.totals?.conversions || 0}
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 bg-app-bg/50 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-text-muted/10 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-6 h-6 text-text-muted/50" />
            </div>
            <p className="text-text-muted text-sm font-medium">
              Sin campañas activas
            </p>
            <p className="text-text-muted/70 text-xs mt-1 max-w-xs mx-auto">
              Conecta Meta Ads o Google Ads en el paso de Integración y despliega una campaña para ver métricas aquí
            </p>
          </div>
        )}
      </div>

      {/* SECTION 3: Checklist de Lanzamiento */}
      <div 
        className="bg-surface border border-border-subtle rounded-xl p-6"
        style={{ animation: 'fadeSlideUp 0.3s ease-out 0.3s both' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-text-main flex items-center gap-2">
            <Check className="w-5 h-5 text-brand-500" />
            Checklist antes de lanzar
          </h3>
          <span className="text-sm text-text-muted">
            {completedChecks}/{launchChecklist.length} completados
          </span>
        </div>

        <div className="space-y-3">
          {launchChecklist.map((item) => (
            <div 
              key={item.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                item.completed ? 'bg-[#00D68F]/5' : 'bg-surface'
              }`}
            >
              {item.manual ? (
                <button
                  onClick={() => toggleManualCheck(item.id)}
                  className="mt-0.5 flex-shrink-0"
                >
                  {item.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-[#00D68F]" />
                  ) : (
                    <Circle className="w-5 h-5 text-text-muted hover:text-brand-500 transition-colors" />
                  )}
                </button>
              ) : (
                <div className="mt-0.5 flex-shrink-0">
                  {item.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-[#00D68F]" />
                  ) : (
                    <Circle className="w-5 h-5 text-text-muted" />
                  )}
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  item.completed ? 'text-text-main' : 'text-text-secondary'
                }`}>
                  {item.label}
                </p>
                <p className="text-xs text-text-muted">{item.description}</p>
              </div>

              {item.manual && (
                <span className="text-xs text-text-muted bg-surface-alt px-2 py-0.5 rounded">
                  Manual
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Checklist Summary Banner */}
        <div className={`mt-4 p-4 rounded-lg ${
          allChecksComplete 
            ? 'bg-[#00D68F]/10 border border-[#00D68F]/20' 
            : 'bg-amber-500/10 border border-amber-500/20'
        }`}>
          {allChecksComplete ? (
            <div className="flex items-center gap-2 text-[#00D68F]">
              <Rocket className="w-5 h-5" />
              <span className="font-medium">¡Todo listo para lanzar! 🚀</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-400">
              <Circle className="w-5 h-5" />
              <span>Faltan {launchChecklist.length - completedChecks} items para estar listo</span>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 4: Magic Approval Link */}
      <div 
        className="bg-surface border border-border-subtle rounded-xl p-6"
        style={{ animation: 'fadeSlideUp 0.3s ease-out 0.35s both' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h3 className="font-medium text-text-main">Compartir con el cliente</h3>
            <p className="text-sm text-text-muted">
              Genera un enlace para que tu cliente revise y apruebe el funnel
            </p>
          </div>
        </div>

        {previewLink ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/f/${previewLink}`}
                className="flex-1 bg-surface-alt border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-text-main"
              />
              <Button
                onClick={copyPreviewLink}
                className="bg-brand-500 hover:bg-brand-hover text-white"
              >
                <CopyIcon className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-muted">Expira en 30 días</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGeneratePreviewLink}
                disabled={generatingLink}
                className="text-text-secondary text-xs"
              >
                {generatingLink ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Regenerar enlace
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={handleGeneratePreviewLink}
            disabled={generatingLink}
            className="w-full bg-brand-500 hover:bg-brand-hover text-white"
          >
            {generatingLink ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            Generar Magic Approval Link · Gratis
          </Button>
        )}
      </div>

      {/* SECTION 5: Resumen del Brief */}
      {funnel?.brief && (
        <div 
          className="bg-surface border border-border-subtle rounded-xl p-6"
          style={{ animation: 'fadeSlideUp 0.3s ease-out 0.4s both' }}
        >
          <h3 className="font-medium text-text-main mb-4">Resumen del Brief</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-text-muted text-xs mb-1">Producto</p>
              <p className="text-text-main font-medium">
                {funnel.brief.productName || funnel.brief.product_name || '-'}
              </p>
            </div>
            <div>
              <p className="text-text-muted text-xs mb-1">Precio</p>
              <p className="text-text-main font-medium">{funnel.brief.price || '-'}</p>
            </div>
            <div>
              <p className="text-text-muted text-xs mb-1">Nicho</p>
              <p className="text-text-main font-medium">{funnel.brief.niche || '-'}</p>
            </div>
            <div>
              <p className="text-text-muted text-xs mb-1">Avatar</p>
              <p className="text-text-main font-medium">
                {funnel.brief.avatarName || funnel.brief.avatar_name || '-'}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-text-muted text-xs mb-1">Promesa principal</p>
              <p className="text-text-main">
                {funnel.brief.mainPromise || funnel.brief.main_promise || '-'}
              </p>
            </div>
            <div>
              <p className="text-text-muted text-xs mb-1">Tono</p>
              <p className="text-text-main font-medium capitalize">
                {funnel.brief.tone || 'Cercano y profesional'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 6: Acciones Rápidas */}
      <div 
        className="bg-surface border border-border-subtle rounded-xl p-6"
        style={{ animation: 'fadeSlideUp 0.3s ease-out 0.45s both' }}
      >
        <h3 className="font-medium text-text-main mb-4">Acciones Rápidas</h3>
        
        {/* Main Download Button */}
        <div className="mb-4">
          <Button
            onClick={handleExportAll}
            disabled={exportingZip}
            className="w-full bg-brand-500 hover:bg-brand-hover text-white py-3"
            data-testid="export-all-zip-btn"
          >
            {exportingZip ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Download className="w-5 h-5 mr-2" />
            )}
            {exportingZip ? 'Generando ZIP...' : 'Descargar todo (ZIP + PDF)'}
          </Button>
          <p className="text-xs text-text-muted text-center mt-2">
            Incluye landings HTML, emails, copies, creativos y resumen PDF
          </p>
        </div>
        
        {/* Section Downloads */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExportSection('landings')}
            disabled={exportingSection === 'landings'}
            className="border-border-subtle text-text-secondary hover:text-text-main text-xs"
            data-testid="export-landings-btn"
          >
            {exportingSection === 'landings' ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <FileText className="w-3 h-3 mr-1" />
            )}
            Landings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExportSection('emails')}
            disabled={exportingSection === 'emails'}
            className="border-border-subtle text-text-secondary hover:text-text-main text-xs"
            data-testid="export-emails-btn"
          >
            {exportingSection === 'emails' ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Mail className="w-3 h-3 mr-1" />
            )}
            Emails
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExportSection('copies')}
            disabled={exportingSection === 'copies'}
            className="border-border-subtle text-text-secondary hover:text-text-main text-xs"
            data-testid="export-copies-btn"
          >
            {exportingSection === 'copies' ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <CopyIcon className="w-3 h-3 mr-1" />
            )}
            Copies
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExportSection('creatives')}
            disabled={exportingSection === 'creatives'}
            className="border-border-subtle text-text-secondary hover:text-text-main text-xs"
            data-testid="export-creatives-btn"
          >
            {exportingSection === 'creatives' ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Image className="w-3 h-3 mr-1" />
            )}
            Creativos
          </Button>
        </div>
        
        {/* Brief PDF Download */}
        <div className="flex items-center gap-2 p-3 bg-app-bg/50 rounded-lg mb-4">
          <FileText className="w-5 h-5 text-brand-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-text-main">Resumen PDF</p>
            <p className="text-xs text-text-muted">Brief profesional con colores de marca</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleExportSection('brief')}
            disabled={exportingSection === 'brief'}
            className="text-brand-500 hover:text-brand-hover"
            data-testid="export-brief-pdf-btn"
          >
            {exportingSection === 'brief' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
        </div>
        
        {/* Other Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={handleDuplicate}
            className="border-border-subtle text-text-secondary hover:text-text-main"
          >
            <CopyIcon className="w-4 h-4 mr-2" />
            Duplicar funnel
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-border-subtle text-text-secondary hover:text-text-main"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ir al Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FunnelDashboard;
